import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { io, Socket } from 'socket.io-client';
import { useRestaurants } from '../../hooks/useRestaurants';
import { api, API_URL } from '../../utils/apiClient';
import type { Order, UpsellRecommendation } from '../../types';
import CreateOrderModal from './CreateOrderModal';
import OrderDetailsModal from './OrderDetailsModal';
import './OrdersPanel.css';

function getDisplayUrl(restaurantId: string, networkUrl?: string): string {
  if (networkUrl) return `${networkUrl}/display?restaurantId=${restaurantId}`;
  const base = window.location.origin;
  if (base.includes('localhost') || base.includes('127.0.0.1')) {
    try {
      const host = new URL(API_URL).hostname;
      if (host !== 'localhost' && host !== '127.0.0.1') {
        return `http://${host}:${window.location.port || 5173}/display?restaurantId=${restaurantId}`;
      }
    } catch {}
  }
  return `${base}/display?restaurantId=${restaurantId}`;
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {}
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

interface OrdersPanelProps {
  restaurantId?: string;
}

function OrdersPanel({ restaurantId: restaurantIdProp }: OrdersPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { restaurants, loading: loadingRestaurants, fetchRestaurants } = useRestaurants();
  const [internalRestaurantId, setInternalRestaurantId] = useState('');
  const selectedRestaurantId = restaurantIdProp || internalRestaurantId;
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState('');
  const [, setSocket] = useState<Socket | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [knownReadyIds, setKnownReadyIds] = useState<Set<string>>(new Set());
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [displayUrl, setDisplayUrl] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [kebabOpen, setKebabOpen] = useState<string | null>(null); // stores orderId of open kebab

  // Close kebab on outside click
  useEffect(() => {
    const handler = () => setKebabOpen(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Separate active orders from completed
  const activeOrders = orders.filter(o => o.status !== 'COMPLETED' && o.status !== 'CANCELLED');
  const completedOrders = orders.filter(o => o.status === 'COMPLETED');
  
  // Show active orders by default, or completed if toggle is on
  const stats = {
    pending: activeOrders.filter(o => o.status === 'PENDING').length,
    preparing: activeOrders.filter(o => o.status === 'PREPARING').length,
    ready: activeOrders.filter(o => o.status === 'READY').length,
    completed: showCompleted ? completedOrders.length : completedCount,
  };

  useEffect(() => {
    fetchRestaurants().then(filtered => {
      if (!restaurantIdProp) setInternalRestaurantId(prev => prev || (filtered[0]?.id ?? ''));
    });
    api.get<{ displayUrl?: string }>('/api/network-info')
      .then(res => { if (res.data.displayUrl) setDisplayUrl(res.data.displayUrl); })
      .catch(() => {});
  }, [fetchRestaurants, restaurantIdProp]);

  useEffect(() => {
    if (selectedRestaurantId) {
      fetchOrders();
      fetchCompletedCount(); // Always fetch completed count separately
      
      // Connect to Socket.io for real-time updates
      const newSocket = io(API_URL);
      newSocket.on('connect', () => {
        newSocket.emit('join-restaurant', selectedRestaurantId);
      });
      newSocket.on('order-status-changed', ({ orderId, status }) => {
        setOrders(prev => {
          // If order was completed and we're viewing active orders, remove it immediately
          if (status === 'COMPLETED' && !showCompleted) {
            setCompletedCount(prevCount => prevCount + 1);
            return prev.filter(o => o.id !== orderId);
          }
          
          // If order was uncompleted (status changed from completed to something else)
          if (status !== 'COMPLETED' && !showCompleted) {
            const wasCompleted = prev.find(o => o.id === orderId && o.status === 'COMPLETED');
            if (wasCompleted) {
              setCompletedCount(prevCount => Math.max(0, prevCount - 1));
            }
          }
          
          // Otherwise, update the status
          return prev.map(o => 
            o.id === orderId ? { ...o, status: status as any } : o
          );
        });
      });
      
      newSocket.on('order-created', (newOrder: any) => {
        if (newOrder.restaurantId === selectedRestaurantId && !showCompleted) {
          setOrders(prev => [newOrder, ...prev]);
        }
      });

      setSocket(newSocket);

      // Auto-refresh every 7 seconds
      const interval = setInterval(() => {
        fetchOrders();
        fetchCompletedCount(); // Also refresh completed count
      }, 7000);

      return () => {
        newSocket.close();
        clearInterval(interval);
      };
    } else {
      setOrders([]);
      setCompletedCount(0);
    }
  }, [selectedRestaurantId, showCompleted]);

  const fetchCompletedCount = async () => {
    if (!selectedRestaurantId.trim()) return;
    try {
      const { data } = await api.get<{ total?: number }>(`/api/orders/restaurant/${selectedRestaurantId}`, {
        params: { status: 'COMPLETED', limit: 1 },
      });
      setCompletedCount(data.total ?? 0);
    } catch {
      // Don't set error state for this
    }
  };

  const fetchOrders = async () => {
    if (!selectedRestaurantId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const statusFilter = showCompleted ? 'COMPLETED' : undefined;
      const { data } = await api.get<{ orders?: Order[] }>(`/api/orders/restaurant/${selectedRestaurantId}`, {
        params: statusFilter ? { status: statusFilter } : {},
      });
      let fetchedOrders = data.orders ?? [];
      if (!showCompleted) {
        fetchedOrders = fetchedOrders.filter(o => o.status !== 'COMPLETED' && o.status !== 'CANCELLED');
      }
      setOrders(fetchedOrders);
      if (showCompleted) setCompletedCount(fetchedOrders.length);
      const readyOrders = (data.orders ?? []).filter(o => o.status === 'READY');
      const newReady = readyOrders.filter(o => !knownReadyIds.has(o.id));
      if (newReady.length > 0) {
        playReadySound();
        setKnownReadyIds(new Set(readyOrders.map(o => o.id)));
      }
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setError(msg ?? 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const showQr = async (orderId: string) => {
    try {
      await api.post(`/api/orders/${orderId}/show-qr`);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, scannedAt: null } : o));
    } catch (err) {
      console.error('Failed to reset QR:', err);
    }
  };

  const updateStatus = async (orderId: string, status: string) => {
    // Optimistic update — change status locally before server confirms
    setOrders(prev => {
      if (status === 'COMPLETED' && !showCompleted) {
        setCompletedCount(c => c + 1);
        return prev.filter(o => o.id !== orderId);
      }
      return prev.map(o => o.id === orderId ? { ...o, status: status as any } : o);
    });
    try {
      await api.patch(`/api/orders/${orderId}/status`, { status });
      fetchOrders();
      if (status === 'COMPLETED') fetchCompletedCount();
    } catch (err: unknown) {
      // Revert on error
      fetchOrders();
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setError(msg ?? 'Failed to update status');
    }
  };

  const playReadySound = () => {
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = 880;

      osc.connect(gain);
      gain.connect(ctx.destination);

      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);

      osc.start();
      osc.stop(ctx.currentTime + 0.6);
    } catch (e) {
      // Sound not supported or blocked
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      PENDING: 'queue',
      PREPARING: 'cooking',
      READY: 'ready',
      COMPLETED: 'done',
      CANCELLED: 'done',
    };
    return map[status] || 'done';
  };

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      PENDING: t('orders.statusInQueue'),
      PREPARING: t('orders.statusPreparing'),
      READY: t('orders.statusReady'),
      COMPLETED: t('orders.statusCompleted'),
      CANCELLED: t('orders.statusCancelled'),
    };
    return map[status] || status;
  };

  const hasPurchasedUpsells = (order: Order): boolean => {
    return order.upsells?.some(u => u.purchasedAt !== null) || false;
  };

  const getPurchasedUpsells = (order: Order): UpsellRecommendation[] => {
    return order.upsells?.filter(u => u.purchasedAt !== null) || [];
  };

  const getUpsellQuantity = (order: Order, productId: string): number => {
    // Count items that are upsells (created after order creation)
    const orderCreatedAt = new Date(order.createdAt).getTime();
    const UPSELL_TIME_THRESHOLD = 5000; // 5 seconds after order creation
    
    return order.items
      .filter(item => {
        if (!item.product || item.product.id !== productId) return false;
        const itemCreatedAt = item.createdAt ? new Date(item.createdAt).getTime() : orderCreatedAt;
        return itemCreatedAt > orderCreatedAt + UPSELL_TIME_THRESHOLD;
      })
      .reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <div className="orders-panel">
      {!restaurantIdProp && (
        <div className="admin-toolbar" style={{ marginBottom: 16 }}>
          <select
            value={selectedRestaurantId}
            onChange={(e) => setInternalRestaurantId(e.target.value)}
            className="admin-restaurant-select"
            disabled={loadingRestaurants}
            style={{ minWidth: 200 }}
          >
            <option value="">{t('orders.selectRestaurant')}</option>
            {restaurants.map(r => (
              <option key={r.id} value={r.id}>
                {r.name} {r._count && `(${r._count.orders} ${t('orders.ordersCount')})`}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="admin-toolbar">
        <motion.button
          onClick={() => setShowCreateModal(true)}
          className="admin-btn admin-btn-amber"
          disabled={!selectedRestaurantId}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
        >
          + {t('orders.createOrder')}
        </motion.button>
        {selectedRestaurantId && (
          <>
            <motion.button
              onClick={() => copyText(getDisplayUrl(selectedRestaurantId, displayUrl))}
              className="admin-btn admin-btn-default"
              title={t('orders.copyDisplayUrl')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('orders.copyDisplayUrl')}
            </motion.button>
            <motion.button
              onClick={() => navigate(`/kitchen?restaurantId=${selectedRestaurantId}`)}
              className="admin-btn admin-btn-default"
              title={t('orders.kitchenView')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              {t('orders.kitchenView')}
            </motion.button>
          </>
        )}
        <motion.button
          onClick={fetchOrders}
          className="admin-btn admin-btn-default"
          disabled={!selectedRestaurantId}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
        >
          {t('common.refresh')}
        </motion.button>
        <div className="admin-toolbar-spacer" />
        <span style={{ fontSize: 12, color: 'var(--a-muted)', fontWeight: 600 }}>
          {lastUpdate ? `${t('orders.lastUpdated')}: ${lastUpdate}` : selectedRestaurantId ? t('orders.loadingOrders') : t('orders.selectRestaurantPrompt')}
        </span>
        <div className="admin-tab-toggle">
          <button
            className={`admin-tab-toggle-btn ${!showCompleted ? 'active' : ''}`}
            onClick={() => setShowCompleted(false)}
          >
            {t('orders.active')} ({activeOrders.length})
          </button>
          <button
            className={`admin-tab-toggle-btn ${showCompleted ? 'active' : ''}`}
            onClick={() => setShowCompleted(true)}
          >
            {t('orders.completed')} ({showCompleted ? completedOrders.length : completedCount})
          </button>
        </div>
      </div>

      {selectedRestaurantId && (
        <div className="admin-qr-banner">
          <span className="admin-qr-label">{t('orders.displayUrlLabel')}</span>
          <span className="admin-qr-url">{getDisplayUrl(selectedRestaurantId, displayUrl)}</span>
          <button
            onClick={() => copyText(getDisplayUrl(selectedRestaurantId, displayUrl))}
            className="admin-qr-copy"
          >
            {t('modals.copy')}
          </button>
        </div>
      )}

      {!showCompleted && (
        <div className="admin-status-pills">
          <div className={`admin-status-pill ${stats.pending > 0 ? 'active' : ''}`}>
            {t('orders.inQueue')}: {stats.pending}
          </div>
          <div className={`admin-status-pill ${stats.preparing > 0 ? 'active' : ''}`}>
            {t('orders.preparing')}: {stats.preparing}
          </div>
          <div className={`admin-status-pill ${stats.ready > 0 ? 'active' : ''}`}>
            {t('orders.ready')}: {stats.ready}
          </div>
          {stats.completed > 0 && (
            <div className="admin-status-pill" onClick={() => setShowCompleted(true)} style={{ cursor: 'pointer' }}>
              {t('orders.completed')}: {stats.completed}
            </div>
          )}
        </div>
      )}

        {error && (
        <div className="error-box">{error}</div>
      )}

      {loading && orders.length === 0 ? (
        <div className="loading-state">{t('orders.loadingOrders')}</div>
      ) : !selectedRestaurantId ? (
        <div className="empty-state">{t('orders.selectRestaurantPrompt')}</div>
      ) : orders.length === 0 ? (
        <div className="empty-state">{t('orders.noOrders')}</div>
      ) : (
        <div className="admin-table-wrap">
          <div className="admin-table-header">
            <div className="admin-th">{t('orders.orderId')}</div>
            <div className="admin-th">{t('orders.orderItems')}</div>
            <div className="admin-th">{t('orders.status')}</div>
            <div className="admin-th">{t('orders.total')}</div>
            <div className="admin-th">{t('orders.created')}</div>
            <div className="admin-th">{t('orders.actions')}</div>
          </div>
          {orders.map((order) => {
            const hasUpsells = hasPurchasedUpsells(order);
            const purchasedUpsells = getPurchasedUpsells(order);
            const orderCreatedAt = new Date(order.createdAt).getTime();
            const UPSELL_TIME_THRESHOLD = 5000;
            const formatCust = (c: Record<string, string | string[]> | null | undefined) => {
              if (!c || !Object.keys(c).length) return '';
              return Object.entries(c).map(([, v]) => Array.isArray(v) ? v.join(', ') : v).filter(Boolean).join(' • ');
            };
            const originalItems = order.items.filter(item => {
              if (!item.product) return false;
              const itemCreatedAt = item.createdAt ? new Date(item.createdAt).getTime() : orderCreatedAt;
              return itemCreatedAt <= orderCreatedAt + UPSELL_TIME_THRESHOLD;
            });
            const groupedItems = originalItems.reduce((acc, item) => {
              const prod = item.product!;
              const key = `${prod.id}::${JSON.stringify((item as any).customization || {})}`;
              if (!acc[key]) {
                acc[key] = {
                  product: { id: prod.id, name: prod.name },
                  quantity: 0,
                  customization: (item as any).customization,
                };
              }
              acc[key].quantity += item.quantity;
              return acc;
            }, {} as Record<string, { product: { id: string; name: string }; quantity: number; customization?: any }>);
            const groupedArray = Object.values(groupedItems);
            const itemsText = groupedArray.map((group, idx) => (
              <span key={group.product.id + (group.customization ? JSON.stringify(group.customization) : '')}>
                {group.product.name}
                {group.customization && Object.keys(group.customization).length > 0 && ` (${formatCust(group.customization)})`}
                {' x'}{group.quantity}
                {idx < groupedArray.length - 1 ? ', ' : ''}
              </span>
            ));

            return (
              <div
                key={order.id}
                className={`admin-order-row ${order.status === 'COMPLETED' ? 'admin-order-row--done' : ''}`}
                onClick={() => {
                  setSelectedOrderId(order.id);
                  setShowOrderDetails(true);
                }}
              >
                <div>
                  <div className="admin-order-id">#{order.id.slice(-6).toUpperCase()}</div>
                  <div className="admin-order-id-short">{order.id.slice(-8)}</div>
                </div>
                <div>
                  <div className="admin-order-items-main">
                    {itemsText}
                    {hasUpsells && purchasedUpsells.length > 0 && (
                      <span className="admin-order-upsell-tag">
                        + {purchasedUpsells.map(u => `${u.product.name} x${getUpsellQuantity(order, u.product.id)}`).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <span className={`admin-status-badge admin-badge-${getStatusClass(order.status)}`}>
                    <span className="admin-status-dot" />
                    {getStatusLabel(order.status)}
                  </span>
                </div>
                <div className="admin-order-total">₸{order.totalAmount.toFixed(2)}</div>
                <div className="admin-order-time">{formatDate(order.createdAt)}</div>
                <div className="admin-row-actions">
                  {order.status === 'PENDING' && (
                    <motion.button
                      className="admin-action-btn admin-action-btn--primary"
                      onClick={(e) => { e.stopPropagation(); updateStatus(order.id, 'PREPARING'); }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {t('orders.startPreparing')}
                    </motion.button>
                  )}
                  {order.status === 'PREPARING' && (
                    <motion.button
                      className="admin-action-btn admin-action-btn--primary"
                      onClick={(e) => { e.stopPropagation(); updateStatus(order.id, 'READY'); }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {t('orders.markReady', 'Готов')}
                    </motion.button>
                  )}
                  {order.status === 'READY' && (
                    <motion.button
                      className="admin-action-btn admin-action-btn--green"
                      onClick={(e) => { e.stopPropagation(); updateStatus(order.id, 'COMPLETED'); }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {t('orders.markCompleted')}
                    </motion.button>
                  )}
                  {(order.status !== 'COMPLETED' && order.status !== 'CANCELLED') && (
                    <div
                      className="admin-kebab-wrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="admin-kebab-btn"
                        onClick={() => setKebabOpen(kebabOpen === order.id ? null : order.id)}
                      >
                        ⋮
                      </button>
                      {kebabOpen === order.id && (
                        <div className="admin-kebab-menu">
                          <button
                            className="admin-kebab-item admin-kebab-item--danger"
                            onClick={() => { updateStatus(order.id, 'CANCELLED'); setKebabOpen(null); }}
                          >
                            {t('orders.cancelOrder', 'Отменить заказ')}
                          </button>
                          {(order.status === 'PENDING' || order.status === 'PREPARING') && (
                            <button
                              className="admin-kebab-item"
                              onClick={() => { updateStatus(order.id, 'READY'); setKebabOpen(null); }}
                            >
                              ⏩ {t('orders.markReady', 'Отметить готовым')}
                            </button>
                          )}
                          {order.status === 'PREPARING' && (
                            <button
                              className="admin-kebab-item"
                              onClick={() => { updateStatus(order.id, 'COMPLETED'); setKebabOpen(null); }}
                            >
                              ✅ {t('orders.markCompleted')}
                            </button>
                          )}
                          {order.scannedAt && (
                            <button
                              className="admin-kebab-item"
                              onClick={() => { showQr(order.id); setKebabOpen(null); }}
                            >
                              {t('orders.showQr', 'Показать QR')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreateModal && (
        <CreateOrderModal
          restaurantId={selectedRestaurantId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchOrders();
          }}
        />
      )}

      {showOrderDetails && selectedOrderId && (
        <OrderDetailsModal
          orderId={selectedOrderId}
          isOpen={showOrderDetails}
          onClose={() => {
            setShowOrderDetails(false);
            setSelectedOrderId(null);
          }}
          onStatusUpdate={fetchOrders}
        />
      )}
    </div>
  );
}

export default OrdersPanel;

